import React, { useState } from 'react';
import { useJobsState, useTriggerSync, useTriggerSportsEventImport, useTriggerTicketmasterEventImport, useAdminUsers, useAdminEntraUsers } from '../hooks/useAdmin';
import { useSession } from '../lib/session';
import { useToast } from '../lib/toast';
import { Icon } from '../components/Icon';
import type { EntraUser, SyncState, User } from '../api/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTs(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function relativeTs(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

type RunStatus = 'success' | 'partial' | 'failed' | null;

function RunStatusBadge({ status }: { status: RunStatus }) {
  if (!status) {
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
        fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
        border: '1px solid var(--border)', color: 'var(--text-subtle)',
        background: 'transparent',
      }}>NEVER RUN</span>
    );
  }
  const cfg: Record<'success' | 'partial' | 'failed', { label: string; style: React.CSSProperties }> = {
    success: {
      label: 'SUCCESS',
      style: { border: '1px solid var(--border)', color: 'var(--text)', background: 'transparent' },
    },
    partial: {
      label: 'PARTIAL',
      style: { border: '1px solid var(--border)', color: 'var(--text-muted)', background: 'transparent' },
    },
    failed: {
      label: 'FAILED',
      style: { border: '1px solid var(--text)', background: 'var(--text)', color: 'var(--bg)' } as React.CSSProperties,
    },
  };
  const { label, style } = cfg[status];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
      ...style,
    }}>{label}</span>
  );
}

// ---------------------------------------------------------------------------
// Job row
// ---------------------------------------------------------------------------

interface JobRowProps {
  label: string;
  description: string;
  state: SyncState | undefined;
  onRun?: (() => void) | undefined;
  isRunning?: boolean;
  enabled?: boolean;
}

function JobRow({ label, description, state, onRun, isRunning, enabled = true }: JobRowProps) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '16px 24px',
      padding: '20px 0',
      borderBottom: '1px solid var(--border)',
      alignItems: 'start',
    }}>
      {/* Info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{label}</span>
          <RunStatusBadge status={(state?.lastRunStatus ?? null) as RunStatus} />
          {!enabled && (
            <span style={{
              display: 'inline-block', padding: '2px 8px', borderRadius: 4,
              fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              border: '1px solid var(--border)', color: 'var(--text-subtle)', background: 'transparent',
            }}>DISABLED</span>
          )}
        </div>

        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{description}</div>

        <div style={{
          display: 'flex', gap: 20, flexWrap: 'wrap',
          fontSize: '0.75rem', color: 'var(--text-subtle)',
        }}>
          <div>
            <span className="uppercase-label" style={{ marginRight: 6 }}>Last run</span>
            <span className="mono" style={{ color: 'var(--text-muted)' }}>
              {state?.lastIncrementalRunAt
                ? `${formatTs(state.lastIncrementalRunAt)} (${relativeTs(state.lastIncrementalRunAt)})`
                : '—'}
            </span>
          </div>
          {state?.lastFullReconciliationAt && (
            <div>
              <span className="uppercase-label" style={{ marginRight: 6 }}>Last full reconcile</span>
              <span className="mono" style={{ color: 'var(--text-muted)' }}>
                {formatTs(state.lastFullReconciliationAt)} ({relativeTs(state.lastFullReconciliationAt)})
              </span>
            </div>
          )}
        </div>

        {state?.lastRunSummary && (
          <div style={{
            fontSize: '0.8125rem', color: 'var(--text-muted)',
            padding: '8px 12px',
            background: 'var(--bg-subtle)',
            borderRadius: 4,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}>
            {state.lastRunSummary}
          </div>
        )}
      </div>

      {/* Action */}
      {onRun && (
        <button
          onClick={onRun}
          disabled={isRunning}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontWeight: 700, fontSize: '0.8125rem', padding: '7px 14px',
            borderRadius: 6, background: 'var(--bg-subtle)', color: 'var(--text)',
            border: '1px solid var(--border)', cursor: isRunning ? 'not-allowed' : 'pointer',
            opacity: isRunning ? 0.5 : 1, whiteSpace: 'nowrap', marginTop: 4,
            transition: 'background 0.12s',
          }}
          onMouseEnter={(e) => { if (!isRunning) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
        >
          {isRunning
            ? <><Icon name="clock" size={14} /> Running…</>
            : <><Icon name="swap" size={14} /> Run now</>}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section shell
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <h2 style={{ margin: '0 0 20px', fontSize: '1.125rem', fontWeight: 700 }}>{title}</h2>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Info row (key → value)
// ---------------------------------------------------------------------------

function InfoRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      gap: 16, padding: '11px 0', borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{ fontWeight: 600, textAlign: 'right', fontSize: '0.875rem' }}
        className={mono ? 'mono' : undefined}
      >
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feature flag row
// ---------------------------------------------------------------------------

function FlagRow({ label, value }: { label: string; value: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 16, padding: '11px 0', borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
        {label}
      </span>
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
        fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
        border: '1px solid',
        borderColor: value ? 'var(--text)' : 'var(--border)',
        background: value ? 'var(--text)' : 'transparent',
        color: value ? 'var(--bg)' : 'var(--text-subtle)',
      }}>
        {value ? 'ENABLED' : 'DISABLED'}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users table
// ---------------------------------------------------------------------------

interface UserRow {
  id: string;
  displayName: string;
  upn: string;
  jobTitle: string | null;
  mobilePhone: string | null;
  hireDate: string | null;
  isActive: boolean;
}

function UsersSection({
  cantoUsers,
  entraUsers,
  isLoading,
}: {
  cantoUsers: User[];
  entraUsers: EntraUser[];
  isLoading: boolean;
}) {
  const entraMap = new Map(entraUsers.map((u) => [u.id, u]));

  const rows: UserRow[] = cantoUsers.map((u) => {
    const entra = entraMap.get(u.entraObjectId);
    return {
      id: u.id,
      displayName: u.displayName,
      upn: entra?.userPrincipalName ?? u.email,
      jobTitle: entra?.jobTitle ?? null,
      mobilePhone: entra?.mobilePhone ?? null,
      hireDate: entra?.employeeHireDate ?? null,
      isActive: u.isActive,
    };
  });

  rows.sort((a, b) => a.displayName.localeCompare(b.displayName));

  const [search, setSearch] = React.useState('');
  const filtered = search
    ? rows.filter((r) =>
        r.displayName.toLowerCase().includes(search.toLowerCase()) ||
        r.upn.toLowerCase().includes(search.toLowerCase()) ||
        (r.jobTitle ?? '').toLowerCase().includes(search.toLowerCase()),
      )
    : rows;

  const thStyle: React.CSSProperties = {
    padding: '10px 12px', textAlign: 'left', fontWeight: 700,
    fontSize: '0.6875rem', letterSpacing: '0.08em', color: 'var(--text-subtle)',
    borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)',
    whiteSpace: 'nowrap',
  };
  const tdStyle: React.CSSProperties = {
    padding: '10px 12px', fontSize: '0.8125rem', borderBottom: '1px solid var(--border)',
    verticalAlign: 'middle',
  };

  return (
    <Section title={`Users${cantoUsers.length ? ` (${cantoUsers.length})` : ''}`}>
      <div style={{ marginBottom: 12 }}>
        <input
          type="search"
          placeholder="Filter by name, UPN, or title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%', maxWidth: 360, padding: '7px 12px',
            border: '1px solid var(--border)', borderRadius: 6,
            background: 'var(--bg-elevated)', color: 'var(--text)',
            fontSize: '0.875rem', outline: 'none',
          }}
        />
      </div>

      <div style={{
        border: '1px solid var(--border)', borderRadius: 8,
        overflow: 'hidden', overflowX: 'auto',
      }}>
        {isLoading ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-subtle)', fontSize: '0.875rem' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            Loading users…
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>NAME</th>
                <th style={thStyle}>UPN</th>
                <th style={thStyle}>TITLE</th>
                <th style={thStyle}>MOBILE</th>
                <th style={thStyle}>HIRE DATE</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>ACTIVE</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-subtle)', padding: '28px 12px' }}>
                    {search ? 'No users match that filter.' : 'No users found.'}
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id} style={{ background: 'var(--bg-elevated)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--bg-elevated)')}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{row.displayName}</td>
                    <td style={{ ...tdStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {row.upn}
                    </td>
                    <td style={{ ...tdStyle, color: row.jobTitle ? 'var(--text)' : 'var(--text-subtle)' }}>
                      {row.jobTitle ?? '—'}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: '0.75rem', color: row.mobilePhone ? 'var(--text-muted)' : 'var(--text-subtle)' }}>
                      {row.mobilePhone ?? '—'}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {row.hireDate
                        ? new Date(row.hireDate).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
                        border: '1px solid',
                        borderColor: row.isActive ? 'var(--text)' : 'var(--border)',
                        background: row.isActive ? 'var(--text)' : 'transparent',
                        color: row.isActive ? 'var(--bg)' : 'var(--text-subtle)',
                      }}>
                        {row.isActive ? 'YES' : 'NO'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdminPage(): React.ReactElement {
  const { config, hasRole } = useSession();
  const { toast } = useToast();
  const { data: jobs, isLoading: jobsLoading, isError: jobsError, refetch: refetchJobs } = useJobsState();
  const { data: cantoUsers, isLoading: usersLoading } = useAdminUsers();
  const { data: entraUsersData, isLoading: entraUsersLoading } = useAdminEntraUsers();
  const syncUsers = useTriggerSync();
  const syncSports = useTriggerSportsEventImport();
  const syncTicketmaster = useTriggerTicketmasterEventImport();

  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  if (!hasRole('Admin', 'Leader')) {
    return (
      <div style={{ padding: '24px 32px 64px', maxWidth: 1080, margin: '0 auto' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderLeft: '3px solid var(--text)', borderRadius: 6,
        }}>
          <Icon name="alert" size={18} />
          <span style={{ fontSize: '0.875rem' }}>You don't have permission to view this page.</span>
        </div>
      </div>
    );
  }

  const handleRefresh = () => {
    void refetchJobs();
    setLastRefreshed(new Date());
  };

  const handleUserSync = (forceFull: boolean) => {
    syncUsers.mutate(forceFull, {
      onSuccess: () => toast.success(forceFull ? 'Full user sync started.' : 'Incremental user sync started.'),
      onError: () => toast.error('Sync failed. Check API logs.'),
    });
  };

  const handleSportsSync = () => {
    syncSports.mutate(undefined, {
      onSuccess: (result) => toast.success(`Sports import: ${result.inserted} inserted, ${result.updated} updated.`),
      onError: () => toast.error('Sports event import failed.'),
    });
  };

  const handleTicketmasterSync = () => {
    syncTicketmaster.mutate(undefined, {
      onSuccess: (result) => toast.success(`Ticketmaster import: ${result.inserted} inserted, ${result.updated} updated.`),
      onError: () => toast.error('Ticketmaster import failed.'),
    });
  };

  return (
    <div style={{ padding: '24px 32px 64px', maxWidth: 1080, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ marginBottom: 40 }}>
        <div className="uppercase-label" style={{ marginBottom: 6 }}>System</div>
        <h1 style={{ margin: 0, fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.02em' }}>
          Admin
        </h1>
        <div style={{ color: 'var(--text-muted)', marginTop: 6, fontSize: '0.9375rem' }}>
          Sync jobs, feature flags, and build info. Changes here affect all users.
        </div>
      </div>

      {/* ── Sync jobs ─────────────────────────────────────── */}
      <Section title="Background jobs">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button
            onClick={handleRefresh}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: 'var(--link)', fontWeight: 700, fontSize: '0.8125rem',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            <Icon name="swap" size={13} />
            Refresh status
            {lastRefreshed && (
              <span style={{ fontWeight: 400, color: 'var(--text-subtle)' }}>
                — refreshed {relativeTs(lastRefreshed.toISOString())}
              </span>
            )}
          </button>
        </div>

        {jobsError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 16,
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderLeft: '3px solid var(--text)', borderRadius: 6, fontSize: '0.875rem',
          }}>
            <Icon name="alert" size={16} />
            Could not load jobs state. The API may be unavailable.
          </div>
        )}

        <div style={{
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '0 20px',
        }}>
          {jobsLoading ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-subtle)', fontSize: '0.875rem' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              Loading job state…
            </div>
          ) : (
            <>
              {/* Entra user sync */}
              <JobRow
                label="Entra user sync"
                description={`Syncs users from Azure Entra ID. Cadence: ${config.jobs.userSync.cadence}`}
                state={jobs?.userSync}
                enabled={config.jobs.userSync.enabled}
                onRun={() => handleUserSync(false)}
                isRunning={syncUsers.isPending}
              />

              {/* User sync — force full */}
              <JobRow
                label="Entra user sync — full reconcile"
                description="Compares all Entra users against all Canto users and repairs any divergence. Slower than incremental."
                state={undefined}
                onRun={() => handleUserSync(true)}
                isRunning={syncUsers.isPending}
              />

              {/* Sports events */}
              <JobRow
                label="Sports event import"
                description={`Imports upcoming local sports events from the configured feed. Cadence: ${config.jobs.sportsEventSync.cadence}`}
                state={jobs?.sportsEventSync}
                enabled={config.jobs.sportsEventSync.enabled}
                onRun={config.jobs.sportsEventSync.enabled ? handleSportsSync : undefined}
                isRunning={syncSports.isPending}
              />

              {/* Ticketmaster */}
              <JobRow
                label="Ticketmaster event import"
                description={`Imports upcoming concerts and events from Ticketmaster for the configured venue. Cadence: ${config.jobs.ticketmasterVenueEventSync.cadence}`}
                state={jobs?.ticketmasterVenueEventSync}
                enabled={config.jobs.ticketmasterVenueEventSync.enabled}
                onRun={config.jobs.ticketmasterVenueEventSync.enabled ? handleTicketmasterSync : undefined}
                isRunning={syncTicketmaster.isPending}
              />
            </>
          )}
        </div>
      </Section>

      {/* ── Feature flags ─────────────────────────────────── */}
      <Section title="Feature flags">
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 14 }}>
          Read-only in v1. These are set at deploy time via app configuration.
        </div>
        <div style={{
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '0 20px',
        }}>
          <FlagRow label="featureFlags.timeslipAdjustmentsEnabled"  value={config.featureFlags.timeslipAdjustmentsEnabled} />
          <FlagRow label="featureFlags.menuPublishToWordpress"       value={config.featureFlags.menuPublishToWordpress} />
          <FlagRow label="featureFlags.ekosCatalogSync"             value={config.featureFlags.ekosCatalogSync} />
        </div>
      </Section>

      {/* ── Build info ────────────────────────────────────── */}
      <Section title="Build &amp; environment">
        <div style={{
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '0 20px',
        }}>
          <InfoRow label="Environment"   value={config.environment}       />
          <InfoRow label="Build version" value={config.buildVersion ?? '—'} mono />
          <InfoRow label="Commit SHA"    value={
            config.buildCommitSha
              ? <code style={{ fontSize: '0.8125rem', background: 'var(--bg-subtle)', padding: '2px 6px', borderRadius: 4 }}>
                  {config.buildCommitSha}
                </code>
              : '—'
          } />
          <InfoRow label="Built at"      value={
            config.builtAt
              ? `${formatTs(config.builtAt)} (${relativeTs(config.builtAt)})`
              : '—'
          } mono />
        </div>
      </Section>

      {/* ── Users ─────────────────────────────────────────── */}
      <UsersSection
        cantoUsers={cantoUsers?.items ?? []}
        entraUsers={entraUsersData?.items ?? []}
        isLoading={usersLoading || entraUsersLoading}
      />

    </div>
  );
}
